import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetClose, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CreditCard,
  Building2,
  User,
  FileText,
  Calendar,
  Hash,
  AlertCircle,
  Pill,
  Receipt,
  X,
} from 'lucide-react';
import { formatToLocalDate, getDefaultContactValue } from '@/lib/utils';
import { clinicPaymentService } from '@/services/clinicPaymentService';
import { useToast } from '@/hooks/use-toast';

interface TransactionDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: string;
}

// Maps the Transaction.revenue_category enum (and a few legacy fallbacks)
// to a human-readable description of *what* the transaction is for.
const REVENUE_CATEGORY_LABELS: Record<string, string> = {
  consulting: 'Consulting',
  program_delivery: 'Program / Guided Order',
  clinic_facilitation: 'Clinic Facilitation Fee',
  dtc_subscription: 'DTC Subscription',
  fulfillment: 'Fulfillment',
  platform: 'Platform Fee',
  visit_billing: 'Visit Billing',
  lab_order: 'Lab Order',
  prescription: 'Prescription',
};

type TxShape = {
  revenue_category?: string;
  invoice?: unknown;
  prescription?: unknown;
  guidedOrder?: { id?: string } | null;
  metadata?: { guided_order_id?: string } | null;
  type?: string;
};

const getTransactionTypeDescription = (tx: TxShape | null): string => {
  if (tx?.revenue_category && REVENUE_CATEGORY_LABELS[tx.revenue_category]) {
    return REVENUE_CATEGORY_LABELS[tx.revenue_category];
  }
  if (tx?.invoice) return 'Invoice Payment';
  if (tx?.prescription) return 'Prescription Payment';
  if (tx?.guidedOrder?.id || tx?.metadata?.guided_order_id) return 'Guided Order Payment';
  if (tx?.type === 'refund' || tx?.type === 'partial_refund') return 'Refund';
  if (tx?.type === 'chargeback') return 'Chargeback';
  return 'Payment';
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'succeeded':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'pending':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'processing':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'refunded':
      return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'failed':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'cancelled':
      return 'bg-slate-50 text-slate-700 border-slate-200';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'payment':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'refund':
      return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'partial_refund':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'chargeback':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const TransactionDetailDrawer: React.FC<TransactionDetailDrawerProps> = ({
  open,
  onOpenChange,
  transactionId,
}) => {
  const { toast } = useToast();
  const [transaction, setTransaction] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getTransactionDetails = async () => {
    if (!transactionId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await clinicPaymentService.getTransactionDetails(transactionId);

      const tx = response?.data?.transaction ?? response?.transaction;
      if (tx) {
        setTransaction(tx);
      } else {
        setError('Transaction not found');
      }
    } catch (err: any) {
      const errorMessage =
        err.message || err.response?.data?.message || 'Failed to load transaction details';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && transactionId) {
      getTransactionDetails();
    } else {
      setTransaction(null);
      setError(null);
    }
  }, [open, transactionId]);

  if (isLoading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'>
          <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
            <Skeleton className='h-5 w-40' />
            <Skeleton className='h-10 w-10 rounded-full' />
          </div>
          <div className='flex items-center justify-between border-b border-slate-100 px-6 py-4'>
            <div className='flex gap-2'>
              <Skeleton className='h-6 w-20 rounded-full' />
              <Skeleton className='h-6 w-20 rounded-full' />
            </div>
            <Skeleton className='h-8 w-24' />
          </div>
          <div className='flex-1 space-y-5 overflow-y-auto px-6 py-6'>
            {[1, 2, 3].map(i => (
              <div key={i} className='overflow-hidden rounded-xl border border-slate-200'>
                <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                  <Skeleton className='h-4 w-40' />
                </div>
                <div className='space-y-3 p-4'>
                  <div className='grid grid-cols-2 gap-4'>
                    <Skeleton className='h-12 w-full' />
                    <Skeleton className='h-12 w-full' />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (error || !transaction) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'>
          <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
            <SheetTitle className='font-semibold text-slate-900' style={{ fontSize: '1.05rem' }}>
              Transaction Details
            </SheetTitle>
            <SheetClose asChild>
              <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
                <X className='h-5 w-5 text-slate-600' />
              </button>
            </SheetClose>
          </div>
          <div className='flex-1 overflow-y-auto px-6 py-6'>
            <div className='rounded-xl border border-red-100 bg-red-50 p-4'>
              <p className='text-sm text-red-700'>{error || 'Transaction not found'}</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  const patient = transaction?.patient;
  const contacts = patient?.contacts || [];
  const email = getDefaultContactValue(contacts, 'email');
  const phone = getDefaultContactValue(contacts, 'phone');
  const address = patient?.addresses?.[0];
  const addressLine1 = address?.line_1;
  const addressLine2 = address?.line_2;
  const city = address?.city;
  const state = address?.stateInfo?.code || address?.state;
  const postalCode = address?.postal_code;
  const isGuidedOrder = !!transaction?.guidedOrder?.id || !!transaction?.metadata?.guided_order_id;
  const shippingCostCents = transaction?.prescription?.shipping_cost_cents ?? 0;
  const shippingCost = shippingCostCents / 100;
  const hasShippingDetails = !!transaction?.prescription;
  const paymentSplitTotal = (transaction?.splits || []).reduce(
    (sum: number, split: any) => sum + (split.amount || 0),
    0
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
          <SheetTitle className='font-semibold text-slate-900' style={{ fontSize: '1.05rem' }}>
            Transaction Details
          </SheetTitle>
          <SheetClose asChild>
            <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
              <X className='h-5 w-5 text-slate-600' />
            </button>
          </SheetClose>
        </div>

        {/* Status Badges and Amount */}
        <div className='flex items-center justify-between border-b border-slate-100 px-6 py-4'>
          <div className='flex items-center gap-2'>
            <Badge
              className={`${getStatusColor(transaction.status)} border px-2.5 py-1 text-xs font-medium capitalize`}
            >
              {transaction.status}
            </Badge>
            {transaction.type && transaction.type !== 'payment' && (
              <Badge
                className={`${getTypeColor(transaction.type)} border px-2.5 py-1 text-xs font-medium capitalize`}
              >
                {transaction.type.replace('_', ' ')}
              </Badge>
            )}
            {/* Friendly description of *what* the transaction is for */}
            <Badge className='border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium capitalize text-slate-700'>
              {getTransactionTypeDescription(transaction)}
            </Badge>
          </div>
          <div className='text-2xl font-bold text-slate-900'>
            {formatCurrency(transaction.amount || 0)}
          </div>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-6 py-6'>
          <div className='space-y-5'>
            {/* Transaction Information */}
            <div className='overflow-hidden rounded-xl border border-slate-200'>
              <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                <h3 className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
                  <Hash className='h-4 w-4 text-slate-500' />
                  Transaction Information
                </h3>
              </div>
              <div className='space-y-4 p-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='mb-1 text-xs text-slate-500'>Transaction ID</p>
                    <p className='break-all font-mono text-sm text-slate-900'>
                      {transaction.id || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className='mb-1 text-xs text-slate-500'>Internal ID</p>
                    <p className='break-all font-mono text-sm text-slate-900'>{transaction.id}</p>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='mb-1 text-xs text-slate-500'>Date & Time</p>
                    <p className='text-sm text-slate-900'>
                      {transaction.processed_at || transaction.created_at
                        ? formatToLocalDate(
                            transaction.processed_at || transaction.created_at,
                            'MM/DD/YYYY, hh:mm a'
                          )
                        : 'N/A'}
                    </p>
                  </div>
                  {transaction.description && (
                    <div>
                      <p className='mb-1 text-xs text-slate-500'>Description</p>
                      <p className='break-words text-sm text-slate-900'>
                        {transaction.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Invoice & Line Items
                Renders for visit-billing / per-invoice transactions where the
                backend returns transaction.invoice (and its lineItems). */}
            {transaction?.invoice && (
              <div className='overflow-hidden rounded-xl border border-slate-200'>
                <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                  <h3 className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
                    <Receipt className='h-4 w-4 text-slate-500' />
                    Invoice & Line Items
                  </h3>
                </div>
                <div className='space-y-4 p-4'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <p className='mb-1 text-xs text-slate-500'>Invoice #</p>
                      <p className='font-mono text-sm text-slate-900'>
                        {transaction.invoice.invoice_number || transaction.invoice.id}
                      </p>
                    </div>
                    <div>
                      <p className='mb-1 text-xs text-slate-500'>Status</p>
                      <Badge
                        className={`${getStatusColor(transaction.invoice.status)} border text-xs capitalize`}
                      >
                        {String(transaction.invoice.status || '').replace('_', ' ')}
                      </Badge>
                    </div>
                    {transaction.invoice.source && (
                      <div>
                        <p className='mb-1 text-xs text-slate-500'>Source</p>
                        <p className='text-sm capitalize text-slate-900'>
                          {String(transaction.invoice.source).replace(/_/g, ' ')}
                        </p>
                      </div>
                    )}
                    {transaction.invoice.issued_at && (
                      <div>
                        <p className='mb-1 text-xs text-slate-500'>Issued</p>
                        <p className='text-sm text-slate-900'>
                          {formatToLocalDate(transaction.invoice.issued_at, 'MM/DD/YYYY, hh:mm a')}
                        </p>
                      </div>
                    )}
                  </div>

                  {Array.isArray(transaction.invoice.lineItems) &&
                    transaction.invoice.lineItems.length > 0 && (
                      <div className='overflow-hidden rounded-lg border border-slate-100'>
                        <div className='grid grid-cols-12 gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500'>
                          <div className='col-span-7'>Description</div>
                          <div className='col-span-1 text-right'>Qty</div>
                          <div className='col-span-2 text-right'>Unit</div>
                          <div className='col-span-2 text-right'>Amount</div>
                        </div>
                        <div className='divide-y divide-slate-100'>
                          {transaction.invoice.lineItems.map((li: {
                            id: string;
                            description: string;
                            quantity?: number | string;
                            unit_price_cents?: number;
                            amount_cents?: number;
                            cpt_code?: string | null;
                          }) => (
                            <div
                              key={li.id}
                              className='grid grid-cols-12 items-center gap-2 px-4 py-3 text-sm text-slate-900'
                            >
                              <div className='col-span-7'>
                                <p className='font-medium'>{li.description}</p>
                                {li.cpt_code && (
                                  <p className='mt-0.5 text-xs text-slate-500'>
                                    CPT {li.cpt_code}
                                  </p>
                                )}
                              </div>
                              <div className='col-span-1 text-right font-mono'>
                                {Number(li.quantity ?? 1)}
                              </div>
                              <div className='col-span-2 text-right font-mono'>
                                {formatCurrency((li.unit_price_cents || 0) / 100)}
                              </div>
                              <div className='col-span-2 text-right font-mono font-medium'>
                                {formatCurrency((li.amount_cents || 0) / 100)}
                              </div>
                            </div>
                          ))}
                        </div>
                        {typeof transaction.invoice.total_cents === 'number' && (
                          <div className='flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-3'>
                            <span className='text-sm font-semibold text-slate-700'>Total</span>
                            <span className='font-mono text-sm font-bold text-slate-900'>
                              {formatCurrency((transaction.invoice.total_cents || 0) / 100)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Payment Information */}
            <div className='overflow-hidden rounded-xl border border-slate-200'>
              <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                <h3 className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
                  <CreditCard className='h-4 w-4 text-slate-500' />
                  Payment Information
                </h3>
              </div>
              <div className='p-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='mb-1 text-xs text-slate-500'>Payment Method</p>
                    <p className='text-sm capitalize text-slate-900'>
                      {transaction.payment_method || 'N/A'}
                    </p>
                  </div>
                  {transaction.card_last4 && (
                    <div>
                      <p className='mb-1 text-xs text-slate-500'>Card</p>
                      <p className='text-sm font-medium text-slate-900'>
                        {transaction.card_brand?.toUpperCase() || 'CARD'} ••••{' '}
                        {transaction.card_last4}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className='mb-1 text-xs text-slate-500'>Currency</p>
                    <p className='text-sm text-slate-900'>{transaction.currency || 'USD'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            {patient && (
              <div className='overflow-hidden rounded-xl border border-slate-200'>
                <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                  <h3 className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
                    <User className='h-4 w-4 text-slate-500' />
                    Customer Information
                  </h3>
                </div>
                <div className='p-4'>
                  <div className='rounded-lg border border-emerald-100 bg-gradient-to-br from-emerald-50/50 to-teal-50/30 p-4'>
                    <p className='font-semibold text-slate-900'>
                      {patient.first_name} {patient.middle_name || ''} {patient.last_name}
                    </p>
                    {email && <p className='mt-1 text-sm text-slate-600'>{email}</p>}
                    {phone && <p className='text-sm text-slate-600'>{phone}</p>}
                    {(addressLine1 || addressLine2 || city || state || postalCode) && (
                      <div className='mt-3 border-t border-emerald-100/50 pt-3'>
                        <p className='text-sm text-slate-600'>
                          {addressLine1}
                          {addressLine2 && <>, {addressLine2}</>}
                        </p>
                        {(city || state || postalCode) && (
                          <p className='text-sm text-slate-600'>
                            {city}
                            {state && <>, {state}</>}
                            {postalCode && <> {postalCode}</>}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Splits */}
            {(transaction?.splits?.length > 0 || hasShippingDetails) && (
              <div className='overflow-hidden rounded-xl border border-slate-200'>
                <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                  <h3 className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
                    <Building2 className='h-4 w-4 text-slate-500' />
                    Payment Splits
                  </h3>
                </div>
                <div className='p-4'>
                  <div className='overflow-hidden rounded-lg border border-slate-100'>
                    <div className='grid grid-cols-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5'>
                      <div className='text-xs font-medium uppercase tracking-wider text-slate-500'>
                        Recipient
                      </div>
                      <div className='text-right text-xs font-medium uppercase tracking-wider text-slate-500'>
                        Amount
                      </div>
                    </div>
                    <div className='divide-y divide-slate-100'>
                      {transaction?.splits?.map((split: any) => (
                        <div key={split.id} className='flex items-center justify-between px-4 py-3'>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                              split.type === 'nova_med_revenue'
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-emerald-50 text-emerald-700'
                            }`}
                          >
                            {split.type === 'nova_med_revenue'
                              ? 'Nimbus Providers Revenue'
                              : split.clinic?.name || 'Clinic Revenue'}
                          </span>
                          <div className='font-mono text-sm font-medium text-slate-900'>
                            {formatCurrency(
                              split.type === 'nova_med_revenue' && !isGuidedOrder
                                ? (split.amount || 0) - shippingCost
                                : split.amount || 0
                            )}
                          </div>
                        </div>
                      ))}
                      {!isGuidedOrder && (
                        <div className='flex items-center justify-between px-4 py-3'>
                          <span className='inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700'>
                            Shipping Fee
                          </span>
                          <div className='font-mono text-sm font-medium text-slate-900'>
                            {formatCurrency(shippingCost)}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className='flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-3'>
                      <span className='text-sm font-semibold text-slate-700'>Total</span>
                      <span className='font-mono text-sm font-bold text-slate-900'>
                        {formatCurrency(paymentSplitTotal)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Prescription Information */}
            {transaction?.prescription && (
              <div className='overflow-hidden rounded-xl border border-slate-200'>
                <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                  <h3 className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
                    <FileText className='h-4 w-4 text-slate-500' />
                    Prescription Information
                  </h3>
                </div>
                <div className='p-4'>
                  <div className='space-y-3 rounded-lg border border-violet-100 bg-violet-50/50 p-4'>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <p className='mb-1 text-xs text-slate-500'>Order ID</p>
                        <p className='font-mono text-sm text-slate-900'>
                          {transaction.prescription.id}
                        </p>
                      </div>
                      <div>
                        <p className='mb-1 text-xs text-slate-500'>Status</p>
                        <Badge
                          className={`${getStatusColor(transaction.prescription.status)} border text-xs capitalize`}
                        >
                          {transaction.prescription.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      {transaction.prescription.practitioner && (
                        <div className='col-span-2'>
                          <p className='mb-1 text-xs text-slate-500'>Practitioner</p>
                          <p className='text-sm text-slate-900'>
                            {transaction.prescription.practitioner.full_name ||
                              `${transaction.prescription.practitioner.first_name} ${transaction.prescription.practitioner.last_name}`}
                            {transaction.prescription.practitioner.npi && (
                              <span className='ml-2 text-xs text-slate-500'>
                                (NPI: {transaction.prescription.practitioner.npi})
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                    {transaction.prescription.created_at && (
                      <div className='border-t border-violet-100 pt-2'>
                        <p className='text-xs text-slate-500'>
                          Created:{' '}
                          {formatToLocalDate(
                            transaction.prescription.created_at,
                            'MM/DD/YYYY, hh:mm a'
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Product/Medication Details */}
            {transaction?.prescription?.medicationRequests &&
              transaction.prescription.medicationRequests.length > 0 && (
                <div className='overflow-hidden rounded-xl border border-slate-200'>
                  <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                    <h3 className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
                      <Pill className='h-4 w-4 text-slate-500' />
                      Products & Medications
                    </h3>
                  </div>
                  <div className='space-y-3 p-4'>
                    {transaction.prescription.medicationRequests.map(
                      (medication: any, index: number) => (
                        <div
                          key={medication.id || index}
                          className='overflow-hidden rounded-lg border border-slate-100'
                        >
                          <div className='bg-slate-50/50 p-4'>
                            <div className='flex items-start justify-between'>
                              <div className='flex items-center gap-3'>
                                <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100'>
                                  <Pill className='h-4 w-4 text-emerald-600' />
                                </div>
                                <div>
                                  <p className='text-sm font-semibold text-slate-900'>
                                    {medication.medication ||
                                      medication.medication_name ||
                                      'Medication'}
                                  </p>
                                  {medication.dose && (
                                    <p className='mt-0.5 text-xs text-slate-500'>
                                      {medication.dose}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {medication.clinicProduct && (
                                <div className='text-right'>
                                  <p className='font-semibold text-slate-900'>
                                    {formatCurrency(
                                      (medication.clinicProduct.selling_price || 0) / 100
                                    )}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {(medication.quantity_authorized ||
                            medication.sig ||
                            medication.day_supply) && (
                            <div className='border-t border-slate-100 p-4'>
                              <div className='grid grid-cols-2 gap-3'>
                                {medication.quantity_authorized && (
                                  <div>
                                    <p className='mb-0.5 text-xs text-slate-500'>Quantity</p>
                                    <p className='text-sm text-slate-900'>
                                      {medication.quantity_authorized}{' '}
                                      {medication.unit_of_measure || ''}
                                    </p>
                                  </div>
                                )}
                                {medication.day_supply && (
                                  <div>
                                    <p className='mb-0.5 text-xs text-slate-500'>Day Supply</p>
                                    <p className='text-sm text-slate-900'>
                                      {medication.day_supply} days
                                    </p>
                                  </div>
                                )}
                                {medication.sig && (
                                  <div className='col-span-2'>
                                    <p className='mb-0.5 text-xs text-slate-500'>Instructions</p>
                                    <p className='text-sm text-slate-900'>{medication.sig}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Failure Information */}
            {(transaction?.failure_code || transaction?.failure_message) && (
              <div className='overflow-hidden rounded-xl border border-red-200'>
                <div className='border-b border-red-200 bg-red-50 px-4 py-3'>
                  <h3 className='flex items-center gap-2 text-sm font-semibold text-red-700'>
                    <AlertCircle className='h-4 w-4' />
                    Failure Details
                  </h3>
                </div>
                <div className='space-y-2 bg-red-50/50 p-4'>
                  {transaction?.failure_code && (
                    <p className='text-sm text-red-700'>
                      <span className='font-medium'>Error Code:</span> {transaction.failure_code}
                    </p>
                  )}
                  {transaction?.failure_message && (
                    <p className='text-sm text-red-700'>
                      <span className='font-medium'>Error Message:</span>{' '}
                      {transaction.failure_message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className='overflow-hidden rounded-xl border border-slate-200'>
              <div className='border-b border-slate-200 bg-slate-50 px-4 py-3'>
                <h3 className='flex items-center gap-2 text-sm font-semibold text-slate-900'>
                  <Calendar className='h-4 w-4 text-slate-500' />
                  Timestamps
                </h3>
              </div>
              <div className='p-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='mb-1 text-xs text-slate-500'>Created At</p>
                    <p className='text-sm text-slate-900'>
                      {transaction?.created_at
                        ? formatToLocalDate(transaction.created_at, 'MM/DD/YYYY, hh:mm a')
                        : 'N/A'}
                    </p>
                  </div>
                  {transaction?.updated_at && (
                    <div>
                      <p className='mb-1 text-xs text-slate-500'>Updated At</p>
                      <p className='text-sm text-slate-900'>
                        {formatToLocalDate(transaction.updated_at, 'MM/DD/YYYY, hh:mm a')}
                      </p>
                    </div>
                  )}
                  {transaction?.processed_at &&
                    transaction.processed_at !== transaction.created_at && (
                      <div>
                        <p className='mb-1 text-xs text-slate-500'>Processed At</p>
                        <p className='text-sm text-slate-900'>
                          {formatToLocalDate(transaction.processed_at, 'MM/DD/YYYY, hh:mm a')}
                        </p>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TransactionDetailDrawer;
